<script setup>
import { ref, reactive } from 'vue';
import TabNavigator from '@/Shared/Navigation/TabNavigator';
import RainbooIcon from '@/Shared/SVG/RainbooIcon';
import ContactPanner from '@/Shared/ContactPanner';
import ReasonCards from '@/Shared/ReasonCards';

let props = defineProps({
    data: Object,
});

let tabNavigation = [
    { name: 'Game Hub', href: route('gaming.gameHub'), current: false },
    { name: 'Game Store', href: route('gaming.gameStore'), current: false },
    { name: 'XWGT', href: route('gaming.xwgt'), current: true },
    { name: 'Marketplace', href: route('gaming.marketplace'), current: false },
    { name: 'Incubation', href: route('gaming.incubation'), current: false },
    { name: 'Hosting', href: route('gaming.hosting'), current: false },
    { name: 'Development', href: route('gaming.development'), current: false },
];
</script>

<template>
    <div class="relative w-full">
        <TabNavigator :tabs="tabNavigation" />

        <h1
            class="m-auto mt-12 max-w-lg px-2 text-center text-3xl font-bold text-white sm:mt-16"
        >
            {{ data.title }} <br />
            {{ data.title_2 }}
        </h1>

        <div class="mt-2 text-center text-xl font-medium text-white">
            {{ data.subtitle }}
        </div>

        <div class="relative">
            <section class="mt-20 py-[50px] lg:mt-40 lg:py-[100px]">
                <div class="text-center lg:text-left">
                    <p class="mb-10 text-3xl font-bold text-white">
                        {{ data.section_1.title }}
                    </p>
                </div>
                <div
                    class="flex grid-cols-1 flex-col items-center gap-4 sm:grid md:gap-8 lg:grid-cols-12 lg:gap-16"
                >
                    <div
                        class="col-span-12 text-center lg:col-span-5 lg:text-left"
                    >
                        <img
                            class="mb:0 mx-auto mb-5 max-h-[394px] rounded-xl lg:mx-0"
                            :src="data.section_1.image"
                            :alt="data.section_1.title"
                        />
                    </div>
                    <div
                        class="col-span-12 text-center lg:col-span-7 lg:text-left"
                    >
                        <div>
                            <p class="mb-3 text-[28px] font-medium text-white">
                                {{ data.section_1.subtitle }}
                            </p>
                        </div>
                        <div>
                            <p class="text-lg text-white text-opacity-80">
                                {{ data.section_1.content }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="py-[50px] lg:py-[100px]">
                <div
                    class="relative left-1/2 w-[calc(100vw-12px)] -translate-x-1/2"
                >
                    <div
                        class="container relative z-10 mx-auto flex grid-cols-1 flex-col items-center gap-4 py-10 sm:grid md:gap-8 lg:grid-cols-12 lg:gap-16"
                    >
                        <div
                            class="col-span-12 px-5 text-center lg:col-span-7 lg:text-left"
                        >
                            <div>
                                <p class="mb-4 text-4xl font-bold text-white">
                                    <span class="mb-4">
                                        {{ data.section_2.title }} <br />
                                    </span>
                                    {{ data.section_2.title_2 }}
                                </p>
                            </div>
                            <div>
                                <p
                                    class="mb-4 text-2xl font-normal text-white text-opacity-80"
                                >
                                    {{ data.section_2.subtitle }}
                                </p>
                            </div>
                            <div>
                                <p class="mb-4 text-3xl font-medium text-white">
                                    {{ data.section_2.content }}
                                </p>
                            </div>
                            <a
                                :href="data.section_2.btn_link"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="gradient-button mb-5 inline-block rounded-[40px] px-4 py-2 text-lg font-semibold text-white duration-300 lg:mb-0"
                            >
                                {{ data.section_2.btn_text }}
                            </a>
                        </div>
                        <div
                            class="z-10 col-span-12 text-center lg:col-span-5 lg:text-left"
                        >
                            <img
                                class="mx-auto max-h-[250px]"
                                :src="data.section_2.image"
                                :alt="data.section_2.title"
                            />
                        </div>
                    </div>
                    <img
                        :src="data.section_2.bg_image"
                        alt="Wodo XWGT Background"
                        class="absolute left-0 top-0 h-full w-full"
                    />
                </div>
            </section>

            <section class="py-[50px] lg:py-[100px]">
                <div class="mt-3">
                    <div
                        class="flex flex-wrap justify-center gap-4 md:gap-8 lg:gap-16"
                    >
                        <div
                            class="relative min-h-[260px] w-[calc(100%-32px)] overflow-hidden rounded-2xl bg-wgn-blue-1 p-6 lg:w-[calc(50%-32px)]"
                            v-for="card in data.section_3.cards"
                        >
                            <div class="relative z-10 text-center">
                                <span
                                    class="inline-block h-8 w-8"
                                    v-html="card.icon"
                                ></span>
                                <h3
                                    class="t mt-5 text-2xl font-medium text-white"
                                >
                                    {{ card.title }}
                                </h3>
                                <p class="mt-2 text-base text-white">
                                    {{ card.content }}
                                </p>
                            </div>
                            <img
                                class="absolute left-0 top-0 h-full w-full"
                                :src="data.section_3.background"
                                alt="XWGT Icons"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section class="py-[50px] lg:py-[100px]">
                <div class="mt-2 w-full border-b border-wgn-purple-4">
                    <RainbooIcon class="m-auto" />
                </div>
            </section>

            <section class="pb-[100px]">
                <div class="flex flex-wrap justify-between pb-2 lg:flex-row">
                    <div
                        class="w-[calc(100%-32px)] rounded-2xl p-6 md:w-[calc(50%-32px)] lg:w-[calc(25%-32px)]"
                        v-for="card in data.section_4.cards"
                    >
                        <div class="relative flex-1 text-center lg:text-left">
                            <div
                                class="wodo-rotate-180 inline-block rounded-[20px] bg-wgn-blue-1 p-5"
                            >
                                <span
                                    class="wodo-rotate-icon inline-block"
                                    v-html="card.icon"
                                ></span>
                            </div>
                            <h3 class="mt-5 text-2xl font-medium text-white">
                                {{ card.title }}
                            </h3>
                            <p
                                class="mt-2 text-base text-white text-opacity-80"
                            >
                                {{ card.content }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section
                class="relative left-1/2 w-[calc(100vw-12px)] -translate-x-1/2 py-[50px] lg:py-[100px]"
            >
                <div class="container relative z-10 mx-auto">
                    <div class="mt-10 grid grid-cols-12">
                        <div class="col-span-10 col-start-2 col-end-12">
                            <div
                                class="flex flex-col items-center justify-start space-y-6 lg:flex-row lg:space-x-10 lg:space-y-0"
                            >
                                <div
                                    class="mb-5 w-full text-center lg:text-left"
                                >
                                    <div
                                        class="gradient-slogan mb-2 text-xl font-semibold uppercase tracking-[1%]"
                                    >
                                        {{ data.section_5.slogan }}
                                    </div>
                                    <div
                                        class="mb-4 text-3xl font-extrabold tracking-[1%] text-wgn-white-1"
                                    >
                                        {{ data.section_5.title }}
                                    </div>
                                    <p
                                        class="mb-2 mt-3 text-white text-opacity-80"
                                    >
                                        {{ data.section_5.content }}
                                    </p>
                                    <p
                                        class="mb-10 mt-3 text-white text-opacity-80"
                                    >
                                        {{ data.section_5.content_2 }}
                                    </p>
                                    <div class="text-center lg:text-left">
                                        <a
                                            :href="data.section_5.btn_link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="transparent-button inline-block rounded-lg px-4 py-2 text-lg font-medium duration-300"
                                        >
                                            {{ data.section_5.btn_text }}
                                        </a>
                                    </div>
                                </div>

                                <div
                                    class="order-first flex w-full items-center justify-center lg:order-last"
                                >
                                    <img
                                        :src="data.section_5.image"
                                        alt="Wodo Cards"
                                        class="pb-10 lg:pb-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <img
                    class="absolute left-0 top-0 h-full w-full"
                    :src="data.section_5.bg_image"
                    alt="Wodo Cards Background"
                />
            </section>

            <div class="mt-20">
                <ContactPanner></ContactPanner>
            </div>
        </div>
    </div>
</template>

<style scoped>
.gradient-slogan {
    background: linear-gradient(
        180deg,
        #9770e5 0%,
        #cebfec 165.81%,
        rgba(238, 237, 240, 0.5) 261.11%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.gradient-button {
    border: 1.5px solid rgba(181, 166, 242, 1);
    background: linear-gradient(90deg, #6100d4 0%, #511d8e 53%, #32006e 100%);
}

.gradient-button:hover {
    border: 1.5px solid rgba(126, 74, 203, 1);
    background: linear-gradient(90deg, #360175 0%, #330d5f 53%, #32006e 100%);
}

.transparent-button {
    color: rgba(179, 155, 242, 1);
    border: 1.5px solid rgba(181, 166, 242, 1);
    background: transparent;
}

.transparent-button:hover {
    background: rgba(65, 0, 142, 1);
    border: 1px solid transparent;
    color: white;
}

.wodo-rotate-180:hover .wodo-rotate-icon {
    transform: rotateY(1turn);
    transition: all 0.8s ease;
}

.crystal-gradient-background {
    background: linear-gradient(90deg, #a15ac3 0%, #8f42bd 44.5%, #7823b6 100%);
}
</style>
